import {List, Map, Record} from 'immutable';
import moment from 'moment-timezone';
import {formValueSelector} from 'redux-form';

import createSelector from 'web-app/util/custom-create-selector';
import * as InstitutionSelectors from 'web-app/react/entities/institutions/selectors';

import * as Constants from './constants';

const BillPayerDetailsData = new Record({
    invoiced: Map(),
    previewInvoices: List(),
    error: null,
    debt: 0,
    overdue: 0,
    overdue30d: 0,
    balance: 0,
});

const BillPayerDetails = new Record({
    billPayer: null,
    details: new BillPayerDetailsData(),
});

const getTotalInvoiced = invoiced => {
    const monthsKeys = Object.keys(invoiced);
    if (!invoiced || !monthsKeys || monthsKeys.length === 0) {
        return null;
    }

    return monthsKeys.reduce((prev, current) => {
        return {
            ...prev,
            [current]: invoiced[current].reduce((p, c) => {
                return p + c.amount;
            }, 0),
        };
    }, {});
};

const addToTotalInvoice = (total, add) => {
    if (!add) {
        return total;
    }

    const keys = Object.keys(add);

    keys.forEach(key => {
        total = total.update(key, 0, value => value + add[key]);
    });

    return total;
};

const addToTotalData = (total, add) => {
    return total.merge({
        debt: total.debt + add.debt,
        overdue: total.overdue + add.overdue,
        overdue30d: total.overdue30d + add.overdue30d,
        balance: total.balance + add.balance,
    });
};

const getInvoices = (invoiced, paidInvoices) => {
    return Object.entries(invoiced).reduce((prev, [key, invoices]) => {
        return {
            ...prev,
            [key]: invoices.map(invoice => ({
                ...invoice,
                paymentStatus:
                    paidInvoices && paidInvoices.indexOf(invoice.id) > -1
                        ? Constants.INVOICE_PAID_STATUS
                        : Constants.INVOICE_UNKNOWN_STATUS,
            })),
        };
    }, {});
};

const getDebtDetailMonths = fetchPeriod => {
    let months = List();

    const currentMonth = moment(fetchPeriod.from).startOf('month');
    const lastDay = moment(fetchPeriod.to);
    while (currentMonth.isBefore(lastDay)) {
        months = months.push(currentMonth.format('YYYY-MM'));
        currentMonth.add(1, 'month');
    }
    return months;
};

export const debtDetailsData = (billPayers, detailsData, fetchPeriod) => {
    let totalInvoiced = Map();
    let totalDetails = new BillPayerDetailsData();

    const data = billPayers
        .map(billPayer => {
            const details = detailsData.get(billPayer.billPayerId);
            if (details) {
                totalInvoiced = addToTotalInvoice(totalInvoiced, getTotalInvoiced(details.invoiced));
            }
            const childDetails = new BillPayerDetails({
                billPayer,
                details: details
                    ? new BillPayerDetailsData({
                          ...details,
                          invoiced: Map(getInvoices(details.invoiced, details.paidInvoices)),
                      })
                    : new BillPayerDetails(),
            });
            totalDetails = addToTotalData(totalDetails, childDetails.details);
            return childDetails;
        })
        .sort((a, b) => {
            if (!a.billPayer.name) {
                return -1;
            }
            if (!b.billPayer.name) {
                return 1;
            }
            return a.billPayer.name.localeCompare(b.billPayer.name);
        });

    totalDetails = totalDetails.merge({
        invoiced: totalInvoiced,
    });

    const totalData = new BillPayerDetails({
        details: totalDetails,
    });

    return {
        data: List([totalData, ...data]),
        months: fetchPeriod ? getDebtDetailMonths(fetchPeriod) : List(),
    };
};

const formSelector = formValueSelector(Constants.DebtFilterFormName);
export const filterType = state => formSelector(state, 'debtTypeFilter');

export const showAutomaticPaymentsFilters = createSelector(
    InstitutionSelectors.canSupportAutomaticPayments,
    canSupportAutomaticPayments => {
        return canSupportAutomaticPayments;
    },
);

export const showManualDirectDebitFilter = createSelector(InstitutionSelectors.hasDirectDebit, hasDirectDebit => {
    return hasDirectDebit;
});

export const debtDetailsFilterInitialValues = createSelector(
    (_, props) => props.withAutomaticPayments,
    (_, props) => props.debtTypeFilter,
    (_, props) => props.availableSourceTypes,
    (withAutomaticPayments, debtTypeFilter, availableSourceTypes) => {
        // Query paremeter passed to the component - hence the 'parsing'.
        const showActivePayments = withAutomaticPayments === 'true';

        const paysWithSourceType = (availableSourceTypes || []).reduce((object, sourceType) => {
            return {...object, [sourceType]: showActivePayments};
        }, {});

        return {
            paysWithSourceType,
            debtTypeFilter: debtTypeFilter ? debtTypeFilter.toUpperCase() : Constants.DebtTypeFilter.ALL,
        };
    },
);
